import * as React from "react";
import { observer } from "mobx-react-lite";
import { GmMigrationAutoAllocationState } from "../../GmMigrationService";
import { Box, Button, Card, FormHelperText, Grid, List, ListItem, ListItemIcon, ListItemText, SvgIcon, TextField, Typography } from "@mui/material";
import { useEffectOnce } from "react-use";
import { AutoAlloc } from "../../../../_proto/galaxymigratepb/galaxy_migrate_autoalloc_pb";
import { DiskIcon } from "../../../../common/CommonIcons";
import { formatKnownDataType, KnownDataType } from "../../../../common/utils/formatter";
import { BlockDeviceInfo } from "../../../../_proto/galaxymigratepb/galaxy_migrate_storage_pb";
import { useMemo, useState } from "react";
import produce from "immer";

// ======================
// SilkSdpAllocateVolumesStep
// ======================
interface VendorAllocateVolumesStepProps {
    allocateFunc: () => Promise<void>;
    state: GmMigrationAutoAllocationState;
}

interface SilkDiskAssignmentConfig {
    capacity: number;
    volumeGroupName: string;
}

export const SilkSdpAllocateVolumesStep: React.FC<VendorAllocateVolumesStepProps> = observer((p) => {
    const state = p.state;
    const allocateNow = p.allocateFunc;

    useEffectOnce(() => {
        for (let device of state.sourceDevices) {
            device.autoAllocParams.setSilksdp(new AutoAlloc.VolumeParams.SilkSdp());
        }
    });

    const getInitialDeviceAssignments = () => {
        const assignments: { [key: string]: SilkDiskAssignmentConfig } = {};
        for (let device of state.sourceDevices) {
            const blockDevice = device.source.getBlockDevice();
            assignments[blockDevice.getDeviceName()] = {
                capacity: blockDevice.getCapacity() / Math.pow(1024, 3),
                volumeGroupName: "",
            };
        }
        return assignments;
    };

    const [deviceConfigs, setDeviceConfigs] = useState<{ [key: string]: SilkDiskAssignmentConfig }>(getInitialDeviceAssignments());

    const setDeviceCapacity = (deviceName: string, capacity: number) => {
        setDeviceConfigs(
            produce((draft) => {
                if (capacity > 0) {
                    draft[deviceName].capacity = capacity;
                } else {
                    draft[deviceName].capacity = null;
                }
            })
        );
        const sourceDeviceInAutoAllocState = state.sourceDevices.find((s) => s.source.getBlockDevice().getDeviceName() === deviceName);
        sourceDeviceInAutoAllocState.autoAllocVolumeCapacity = capacity;
    };

    const setDeviceVolumeGroupName = (deviceName: string, volumeGroupName: string) => {
        setDeviceConfigs(
            produce((draft) => {
                draft[deviceName].volumeGroupName = volumeGroupName;
            })
        );
        const sourceDeviceInAutoAllocState = state.sourceDevices.find((s) => s.source.getBlockDevice().getDeviceName() === deviceName);
        sourceDeviceInAutoAllocState.autoAllocParams.getSilksdp().setVolumeGroupName(volumeGroupName);
    };

    const getHasError = () => {
        if (state.allowSmallerDestinations) {
            return false;
        }
        for (let device in deviceConfigs) {
            const blockDevice = state.sourceDevices.find((d) => d.source.getBlockDevice().getDeviceName() === device);
            if (deviceConfigs[device].capacity < blockDevice?.source.getBlockDevice().getCapacity() / Math.pow(1024, 3)) {
                return true;
            }
        }
        return false;
    };

    return (
        <>
            <Typography color={"textSecondary"}>
                {`Destination Volumes will be allocated from the connected storage to match the following source volumes`}
            </Typography>
            <br />
            <List>
                {state.sourceDevices.map((device) => {
                    const blockDevice = device.source.getBlockDevice();
                    return (
                        <SilkSdpDiskCard
                            state={state}
                            blockDevice={blockDevice}
                            setDeviceVolumeGroupName={setDeviceVolumeGroupName}
                            currentDeviceVolumeGroupName={deviceConfigs[blockDevice.getDeviceName()].volumeGroupName}
                            setDeviceCapacity={setDeviceCapacity}
                            currentDeviceCapacity={deviceConfigs[blockDevice.getDeviceName()].capacity}
                        />
                    );
                })}
            </List>
            <Box pt={2} pb={2}>
                <Button color={"primary"} variant={"contained"} onClick={allocateNow} disabled={getHasError()}>
                    {`Allocate Volumes (${state.sourceDevices.length})`}
                </Button>
            </Box>
        </>
    );
});

// ======================
// PureCbsDiskCard
// ======================

interface SilkSdpDiskCardProps {
    state: GmMigrationAutoAllocationState;
    blockDevice: BlockDeviceInfo;
    currentDeviceCapacity: number;
    setDeviceCapacity: (deviceName: string, capacity: number) => void;
    currentDeviceVolumeGroupName: string;
    setDeviceVolumeGroupName: (deviceName: string, volumeGroupName: string) => void;
}

const SilkSdpDiskCard: React.FC<SilkSdpDiskCardProps> = observer((p) => {
    const { state, blockDevice, currentDeviceCapacity, setDeviceCapacity, currentDeviceVolumeGroupName, setDeviceVolumeGroupName } = p;

    const defaultHelperText = "Set a custom disk size.";

    const [capacityHelperText, setCapacityHelperText] = useState(defaultHelperText);

    const getCapacityError = () => {
        if (state.allowSmallerDestinations) {
            return false;
        } else {
            return currentDeviceCapacity < blockDevice.getCapacity() / Math.pow(1024, 3);
        }
    };

    return (
        <Card sx={{ mb: 2 }}>
            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <ListItem key={blockDevice.getDeviceName()}>
                        <ListItemIcon>
                            <DiskIcon />
                        </ListItemIcon>
                        <ListItemText
                            primary={`${blockDevice.getDeviceName()} (${blockDevice.getDeviceType()})`}
                            secondary={formatKnownDataType(blockDevice.getCapacity(), KnownDataType.CAPACITY)}
                        />
                    </ListItem>
                </Grid>
                <Grid item xs={12} md={8}>
                    <Grid container spacing={2} justifyContent={"flex-end"}>
                        <Grid item>
                            <Box p={2}>
                                <TextField
                                    fullWidth
                                    label={"Disk Size"}
                                    variant={"filled"}
                                    error={getCapacityError()}
                                    value={currentDeviceCapacity}
                                    type={"number"}
                                    onChange={(e) => {
                                        setDeviceCapacity(blockDevice.getDeviceName(), Number(e.target.value));
                                        if (!state.allowSmallerDestinations && Number(e.target.value) < blockDevice.getCapacity() / Math.pow(1024, 3)) {
                                            setCapacityHelperText("Must be greater than or equal to source volume size.");
                                        } else {
                                            setCapacityHelperText(defaultHelperText);
                                        }
                                    }}
                                    helperText={capacityHelperText}
                                    InputProps={{ endAdornment: <Typography>{`GiB`}</Typography> }}
                                />
                            </Box>
                        </Grid>
                        <Grid item>
                            <Box p={2}>
                                <TextField
                                    fullWidth
                                    label={"Volume Group Name"}
                                    variant={"filled"}
                                    value={currentDeviceVolumeGroupName}
                                    onChange={(e) => {
                                        setDeviceVolumeGroupName(blockDevice.getDeviceName(), e.target.value);
                                    }}
                                    helperText={"If not specified, default will be used."}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Card>
    );
});
